import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.height || 'auto'};
  background-color: ${props => (props.placeholderImage ? (props.placeholderColor || '#f5f5f5') : 'transparent')};
  background-image: ${props => props.placeholderImage ? `url("${props.placeholderImage}")` : 'none'};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: ${props => props.objectFit || 'cover'};
    transition: opacity ${props => (props.$critical ? 0.5 : 0.35)}s ease-in-out;
    
    &.loading {
      opacity: 0;
    }
    
    &.loaded {
      opacity: 1;
    }
  }
`;

const SkeletonPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    #e8e8e8 0%,
    #e8e8e8 35%,
    #f0f0f0 50%,
    #e8e8e8 65%,
    #e8e8e8 100%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.2s ease-in-out infinite;
  
  @keyframes skeleton-shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const BlurPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  filter: blur(20px);
  transform: scale(1.1);
  opacity: ${props => props.loaded ? 0 : 0.6};
  transition: opacity 0.3s ease-in-out;
  pointer-events: none;
`;

/**
 * Optimized LazyImage component with:
 * - Intersection Observer lazy loading (300px margin)
 * - Immediate loading for above-the-fold images
 * - Critical image support for instant loading
 * - Placeholder image support for improved LCP
 * - Blur placeholder effect (optional)
 * - Width/height attributes to prevent layout shift
 * - Loading state management
 * - Error handling
 */
const LazyImage = ({
  src,
  alt = '',
  height,
  width,
  objectFit = 'cover',
  placeholderColor = '#f5f5f5',
  placeholderImage = null, // Optional placeholder image URL for improved LCP
  useBlurPlaceholder = false,
  className = '',
  style = {},
  onLoad,
  onError,
  critical = false, // If true, load immediately without waiting for visibility check
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(critical); // Start as true for critical images
  const [imageSrc, setImageSrc] = useState(null);
  const [placeholderLoaded, setPlaceholderLoaded] = useState(false);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!src) return;

    // Critical images load immediately
    if (critical) {
      setIsInView(true);
      return;
    }

    // Check if element is already visible (above the fold)
    const checkVisibility = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
      }
      return false;
    };

    // Check immediately - if visible, load right away
    if (checkVisibility()) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '500px', // Start loading 500px before image enters viewport
        threshold: 0.01
      }
    );

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      if (currentContainer) {
        observer.unobserve(currentContainer);
      }
    };
  }, [src, critical]);

  // Load placeholder image immediately if provided
  useEffect(() => {
    if (!placeholderImage) {
      setPlaceholderLoaded(true); // No placeholder, consider it "loaded" to show color
      return;
    }

    const placeholderImg = new Image();

    // Preload placeholder with high priority for critical images
    if (critical && placeholderImg.fetchPriority !== undefined) {
      placeholderImg.fetchPriority = 'high';
    }

    const handlePlaceholderLoad = () => {
      setPlaceholderLoaded(true);
    };

    const handlePlaceholderError = () => {
      setPlaceholderLoaded(true); // Still show color if placeholder fails
    };

    placeholderImg.onload = handlePlaceholderLoad;
    placeholderImg.onerror = handlePlaceholderError;
    placeholderImg.src = placeholderImage;
  }, [placeholderImage, critical]);

  // Load image when in view
  useEffect(() => {
    if (!isInView || !src) return;

    const img = new Image();

    // Add fetchpriority for critical images
    if (critical && img.fetchPriority !== undefined) {
      img.fetchPriority = 'high';
    }

    const handleLoad = () => {
      setImageSrc(img.src);
      setIsLoaded(true);
      if (onLoad) onLoad();
    };

    const handleError = () => {
      setImageSrc(src); // Show original anyway
      if (onError) onError();
    };

    img.onload = handleLoad;
    img.onerror = handleError;
    img.src = src; // Load original image directly
  }, [isInView, src, critical, onLoad, onError]);

  return (
    <ImageContainer
      ref={containerRef}
      height={height}
      width={width}
      placeholderColor={placeholderColor}
      placeholderImage={placeholderImage && placeholderLoaded ? placeholderImage : null}
      objectFit={objectFit}
      $critical={critical}
      className={className}
      style={style}
      {...props}
    >
      {!imageSrc && (!placeholderImage || !placeholderLoaded) && <SkeletonPlaceholder />}
      {useBlurPlaceholder && src && (
        <BlurPlaceholder src={src} loaded={isLoaded} />
      )}
      {imageSrc && (
        <img
          ref={imgRef}
          src={imageSrc}
          alt={alt}
          width={width}
          height={height}
          className={isLoaded ? 'loaded' : 'loading'}
          onLoad={() => setIsLoaded(true)}
          onError={() => {
            if (onError) onError();
          }}
          loading={critical ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={critical ? "high" : "auto"}
        />
      )}
    </ImageContainer>
  );
};

export default LazyImage;

